import { useCallback, useEffect, useState } from 'react';
import type { CoreOrganization } from '@/lib/zifekCore';
import {
  createPerformanceTarget,
  deletePerformanceTarget,
  fetchPerformanceStats,
  listPerformanceTargets,
  SCALE_PILLARS,
  type CorePerformanceTarget,
} from '@/lib/corePerformance';

interface Props {
  commerceId: number;
  org: CoreOrganization;
}

const INPUT = 'w-full px-3 py-2 rounded-md border border-background-200/70 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-colors';

const CATEGORY_CLASS: Record<string, string> = {
  Échelle: 'bg-primary-100 text-primary-900',
  Latence: 'bg-accent-100 text-accent-900',
  Fiabilité: 'bg-secondary-100 text-secondary-900',
};

function formatTarget(t: CorePerformanceTarget): string {
  const v = t.target_value ?? 0;
  if (v >= 1000000) return `${(v / 1000000).toLocaleString('fr-FR')} M`;
  if (v >= 1000) return `${(v / 1000).toLocaleString('fr-FR')} k`;
  return v.toLocaleString('fr-FR');
}

export default function PerformanceTab({ org }: Props) {
  const [targets, setTargets] = useState<CorePerformanceTarget[]>([]);
  const [stats, setStats] = useState({ targets: 0, scaleData: 0, latencyData: 0, reliabilityData: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState<number | null>(null);
  const [form, setForm] = useState({ metricKey: '', label: '', category: 'Échelle', unit: 'comptes', targetValue: 0, horizon: 'Permanent' });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [t, s] = await Promise.all([listPerformanceTargets(), fetchPerformanceStats()]);
      setTargets(t);
      setStats(s);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Chargement impossible.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const onAdd = async () => {
    if (!form.metricKey.trim() || !form.label.trim()) { setError('Clé et libellé sont requis.'); return; }
    setSaving(true);
    setError('');
    try {
      await createPerformanceTarget(form);
      setForm({ metricKey: '', label: '', category: 'Échelle', unit: 'comptes', targetValue: 0, horizon: 'Permanent' });
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Création impossible.');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (t: CorePerformanceTarget) => {
    setBusy(t.id);
    try { await deletePerformanceTarget(t.id); await load(); } catch (err) { setError(err instanceof Error ? err.message : 'Erreur.'); } finally { setBusy(null); }
  };

  return (
    <div className="space-y-5">
      <p className="text-xs text-foreground-500 max-w-3xl">
        <span className="font-semibold text-foreground-700">Performance</span> : le socle doit soutenir
        progressivement des milliers d’entreprises, des centaines de milliers d’utilisateurs et des millions
        d’enregistrements. L’architecture n’est <span className="font-semibold text-foreground-700">jamais</span>
        construite autour d’un nombre fixe de clients.
      </p>

      {error && <div className="p-3 bg-red-50 border border-red-200 rounded-md text-xs text-red-700">{error}</div>}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <div className="rounded-lg border bg-background-50 border-background-200/70 p-4">
          <i className="ri-bar-chart-2-line text-lg text-primary-600"></i>
          <p className="text-lg font-bold text-foreground-900 mt-1">{stats.targets}</p>
          <p className="text-xs text-foreground-500">Cibles</p>
        </div>
        <div className="rounded-lg border bg-background-50 border-background-200/70 p-4">
          <i className="ri-line-chart-line text-lg text-primary-600"></i>
          <p className="text-lg font-bold text-foreground-900 mt-1">{stats.scaleData}</p>
          <p className="text-xs text-foreground-500">Échelle</p>
        </div>
        <div className="rounded-lg border bg-background-50 border-background-200/70 p-4">
          <i className="ri-timer-line text-lg text-accent-600"></i>
          <p className="text-lg font-bold text-foreground-900 mt-1">{stats.latencyData}</p>
          <p className="text-xs text-foreground-500">Latence</p>
        </div>
        <div className="rounded-lg border bg-background-50 border-background-200/70 p-4">
          <i className="ri-shield-check-line text-lg text-secondary-600"></i>
          <p className="text-lg font-bold text-foreground-900 mt-1">{stats.reliabilityData}</p>
          <p className="text-xs text-foreground-500">Fiabilité</p>
        </div>
      </div>

      <div className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden">
        <div className="px-4 py-3 border-b border-background-200/60 flex items-center gap-2">
          <span className="w-8 h-8 rounded-lg bg-primary-100 text-primary-700 flex items-center justify-center"><i className="ri-focus-3-line"></i></span>
          <h4 className="text-sm font-semibold text-foreground-900">Cibles de montée en charge</h4>
        </div>
        {loading ? (
          <div className="flex items-center justify-center py-12"><i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i></div>
        ) : (
          <div className="divide-y divide-background-200/60">
            {targets.map((t) => (
              <div key={t.id} className="px-4 py-3 flex items-center gap-3 flex-wrap">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-foreground-800">
                    {t.label}
                    <span className={`ml-2 text-[10px] px-2 py-0.5 rounded-full ${CATEGORY_CLASS[t.category || ''] || 'bg-background-100 text-foreground-600'}`}>{t.category}</span>
                  </p>
                  <p className="text-[11px] text-foreground-400">{t.metric_key} · {t.horizon}{t.notes ? ` · ${t.notes}` : ''}</p>
                </div>
                <p className="text-sm font-bold text-foreground-900 whitespace-nowrap">{formatTarget(t)} <span className="text-[11px] font-normal text-foreground-500">{t.unit}</span></p>
                <button onClick={() => remove(t)} disabled={busy === t.id} className="px-2.5 py-1 rounded-full bg-red-100 text-red-800 text-[11px] font-medium whitespace-nowrap cursor-pointer disabled:opacity-40">Supprimer</button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-background-50 border border-background-200/70 rounded-lg p-4">
          <h4 className="text-sm font-semibold text-foreground-900 mb-3 flex items-center gap-2"><i className="ri-layout-grid-line text-primary-600"></i>Piliers d’architecture</h4>
          <div className="space-y-3">
            {SCALE_PILLARS.map((p) => (
              <div key={p.key} className="rounded-lg border border-background-200/70 bg-background-100/60 p-3">
                <p className="text-xs font-semibold text-foreground-800 flex items-center gap-2"><i className={`${p.icon} text-primary-600`}></i>{p.label}</p>
                <ul className="mt-1.5 space-y-1">
                  {p.points.map((pt, i) => (
                    <li key={i} className="text-[11px] text-foreground-500 flex items-start gap-1.5"><i className="ri-checkbox-blank-circle-line text-[7px] mt-1.5"></i>{pt}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden">
          <div className="px-4 py-3 border-b border-background-200/60 flex items-center gap-2">
            <span className="w-8 h-8 rounded-lg bg-accent-100 text-accent-700 flex items-center justify-center"><i className="ri-add-line"></i></span>
            <h4 className="text-sm font-semibold text-foreground-900">Ajouter une cible</h4>
          </div>
          <div className="p-4 space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <input className={INPUT} placeholder="Clé (ex : businesses)" value={form.metricKey} onChange={(e) => setForm({ ...form, metricKey: e.target.value })} />
              <input className={INPUT} placeholder="Libellé" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <select className={INPUT} value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
                <option value="Échelle">Échelle</option>
                <option value="Latence">Latence</option>
                <option value="Fiabilité">Fiabilité</option>
              </select>
              <input className={INPUT} placeholder="Unité" value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} />
              <input type="number" className={INPUT} placeholder="Valeur cible" value={form.targetValue} onChange={(e) => setForm({ ...form, targetValue: Number(e.target.value) })} />
            </div>
            <input className={INPUT} placeholder="Horizon (ex : Année 2)" value={form.horizon} onChange={(e) => setForm({ ...form, horizon: e.target.value })} />
            <button onClick={onAdd} disabled={saving} className="px-5 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer disabled:opacity-50 flex items-center gap-2">
              {saving ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-add-line"></i>} Ajouter
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}